import { useAuth } from "./hooks/useAuth";
import { getCfAccessJwtFromCookie } from "./cf-access-config";

// API client configuration for attorney services
export interface ApiConfig {
	baseURL: string;
	timeout: number;
	retries: number;
}

// Default configuration
const DEFAULT_CONFIG: ApiConfig = {
	baseURL: import.meta.env.VITE_ATTORNEY_API_URL || "",
	timeout: 30000,
	retries: 3,
};

// Custom error class for API errors
export class ApiError extends Error {
	constructor(
		message: string,
		public status: number,
		public data?: unknown,
	) {
		super(message);
		this.name = "ApiError";
	}
}

// HTTP client with authentication
class AuthenticatedHttpClient {
	private config: ApiConfig;
  private readonly accessTokenProvider: () => string | null;

	constructor(config: Partial<ApiConfig> = {}, accessTokenProvider: () => string | null = getCfAccessJwtFromCookie) {
		this.config = { ...DEFAULT_CONFIG, ...config };
    this.accessTokenProvider = accessTokenProvider;
	}

    private getAuthHeaders(): Record<string, string> {
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        const token = this.getAccessToken();
        if (token) headers["CF-Access-JWT-Assertion"] = token;
        return headers;
    }

	private async makeRequest<T>(
		method: string,
		url: string,
		data?: unknown,
		options: RequestInit = {},
	): Promise<T> {
		const fullUrl = url.startsWith("http")
			? url
			: `${this.config.baseURL}${url}`;

		const headers = {
			...this.getAuthHeaders(),
			...options.headers,
		};

		const requestOptions: RequestInit = {
			method,
			headers,
			...options,
		};

		if (data && (method === "POST" || method === "PUT" || method === "PATCH")) {
			requestOptions.body = JSON.stringify(data);
		}

		let lastError: Error = new Error("Request failed");

		for (let attempt = 0; attempt <= this.config.retries; attempt++) {
			try {
				const controller = new AbortController();
				const timeoutId = setTimeout(
					() => controller.abort(),
					this.config.timeout,
				);

            const methodU = (requestOptions.method || 'GET').toUpperCase();
            const headers: Record<string, string> = { ...(requestOptions.headers as Record<string,string>), ...this.getAuthHeaders() };
            if (["POST","PUT","PATCH","DELETE"].includes(methodU)) {
                const csrfName = (import.meta.env.VITE_CSRF_HEADER_NAME as string | undefined) || 'X-CSRF-TOKEN';
                const csrf = this.getCsrfToken();
                if (csrf) headers[csrfName] = csrf;
            }
            const response = await fetch(fullUrl, {
                ...requestOptions,
                headers,
                credentials: 'include',
                signal: controller.signal,
            });

				clearTimeout(timeoutId);

				if (!response.ok) {
					let errorData: unknown;
					try {
						errorData = await response.json();
					} catch {
						errorData = { message: "An error occurred" };
					}

					const message =
						typeof errorData === "object" &&
						errorData !== null &&
						"message" in errorData
							? String((errorData as { message?: unknown }).message)
							: undefined;
					throw new ApiError(
						message || `HTTP ${response.status}`,
						response.status,
						errorData,
					);
				}

				return await response.json();
			} catch (error) {
				lastError = error as Error;

            // On 403, refresh CSRF and retry once
            if (error instanceof ApiError && error.status === 403) {
                await this.refreshCsrfToken();
                continue;
            }
            // Don't retry on client errors (4xx)
            if (
                error instanceof ApiError &&
                error.status >= 400 &&
                error.status < 500
            ) {
                throw error;
            }

				// Don't retry on the last attempt
				if (attempt === this.config.retries) {
					break;
				}

				// Wait before retrying (exponential backoff)
				await new Promise((resolve) =>
					setTimeout(resolve, 2 ** attempt * 1000),
				);
			}
		}

		throw lastError;
	}

	async get<T>(url: string, options?: RequestInit): Promise<T> {
		return this.makeRequest<T>("GET", url, undefined, options);
	}

	async post<T>(
		url: string,
		data?: unknown,
		options?: RequestInit,
	): Promise<T> {
		return this.makeRequest<T>("POST", url, data, options);
	}

	async put<T>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
		return this.makeRequest<T>("PUT", url, data, options);
	}

	async patch<T>(
		url: string,
		data?: unknown,
		options?: RequestInit,
	): Promise<T> {
		return this.makeRequest<T>("PATCH", url, data, options);
	}

    async delete<T>(url: string, options?: RequestInit): Promise<T> {
        return this.makeRequest<T>("DELETE", url, undefined, options);
    }

    getAccessToken(): string | null {
        try {
            const token = this.accessTokenProvider?.();
            return token ? token : null;
        } catch {
            return null;
        }
    }

    private getCsrfToken(): string | null {
        if (typeof document === 'undefined') return null;
        const name = (import.meta.env.VITE_CSRF_COOKIE_NAME as string | undefined) || 'CSRF-TOKEN';
        const match = document.cookie
            .split(';')
            .map((s) => s.trim())
            .find((c) => c.startsWith(`${name}=`));
        if (!match) return null;
        try { return decodeURIComponent(match.split('=')[1] || ''); } catch { return match.split('=')[1] || ''; }
    }

    private async refreshCsrfToken(): Promise<void> {
        const baseUrl = this.config.baseURL?.trim();
        if (!baseUrl) return;
        if (typeof fetch === 'undefined') return;
        const target = `${baseUrl.replace(/\/$/, '')}/csrf-token`;
        try { await fetch(target, { credentials: 'include' }); } catch {}
    }
}

// Create singleton instance
const httpClient = new AuthenticatedHttpClient();

// Attorney API client
export class AttorneyApiClient {
	private client: AuthenticatedHttpClient;

	constructor(client: AuthenticatedHttpClient = httpClient) {
		this.client = client;
	}

	// Form responses endpoints
	async getFormResponses(
		org: string,
		slug: string,
		params: {
			page?: number;
			pageSize?: number;
		} = {},
	) {
		const queryParams = new URLSearchParams();
		if (params.page) queryParams.append("page", params.page.toString());
		if (params.pageSize)
			queryParams.append("pageSize", params.pageSize.toString());

		const queryString = queryParams.toString();
		const url = `/api/attorneys/forms/${org}/${slug}/responses${queryString ? `?${queryString}` : ""}`;

		return this.client.get<{
			items: Array<{
				id: string;
				formId: string;
				submittedAt: string;
				data: Record<string, unknown>;
				files: Array<{
					name: string;
					size: number;
					contentType: string;
					url?: string;
				}>;
				status: string;
			}>;
			page: number;
			pageSize: number;
			total: number;
			totalPages: number;
		}>(url);
	}

	// Health check
	async healthCheck() {
		return this.client.get<{ status: string }>("/health");
	}
}

// React Query integration
export function useApiClient() {
	return new AttorneyApiClient();
}

// Export singleton instance
export const apiClient = new AttorneyApiClient();

// Utility hook for API calls with auth context
export function useAuthenticatedApi() {
	const { user, isAuthenticated } = useAuth();

	if (!isAuthenticated) {
		throw new Error("User must be authenticated to use the API");
	}

	return {
		client: apiClient,
		user,
	};
}

// Error handling utilities
export function isApiError(error: unknown): error is ApiError {
	return error instanceof ApiError;
}

export function handleApiError(error: unknown): string {
	if (isApiError(error)) {
		switch (error.status) {
			case 401:
				return "Authentication required. Please log in again.";
			case 403:
				return "You do not have permission to perform this action.";
			case 404:
				return "The requested resource was not found.";
			case 422:
				return "Invalid data provided.";
			case 500:
				return "A server error occurred. Please try again later.";
			default:
				return error.message || "An unexpected error occurred.";
		}
	}

	if (error instanceof Error) {
		return error.message;
	}

	return "An unexpected error occurred.";
}

// Request/response interceptors (for future use with generated client)
type RequestConfig = { headers?: Record<string, string> } & Record<
	string,
	unknown
>;

export const requestInterceptor = (config: RequestConfig) => {
	const token = httpClient.getAccessToken();
	if (token) {
		config.headers = {
			...config.headers,
			"CF-Access-JWT-Assertion": token,
		};
	}
	return config;
};

export const responseInterceptor = <T>(response: T): T => response;

export const errorInterceptor = (error: unknown) =>
	Promise.reject(handleApiError(error));

export { AuthenticatedHttpClient };
